import * as tslib_1 from "tslib";
import * as Logger from 'js-logger';
import { BehaviorSubject, EMPTY, interval } from 'rxjs';
import { delay, filter, first, tap } from 'rxjs/operators';
import { MenuOrientation } from '@app/platform/platform';
import { SettingKeys } from '@app/sync/settings.service';
import { SyncConfigDownloadEvent, SyncEndEvent } from '@app/sync/sync.service.base';
var BootstrapServiceBase = /** @class */ (function () {
    function BootstrapServiceBase(sync, settings, platform, time, controllerClient) {
        this.sync = sync;
        this.settings = settings;
        this.platform = platform;
        this.time = time;
        this.controllerClient = controllerClient;
        this.dhcpError$ = EMPTY;
        this.messageText$ = new BehaviorSubject(['Starte']);
        this.logger = Logger.get('bootstrap');
    }
    /**
     * Called before anything else after angular has finished booting.
     */
    BootstrapServiceBase.prototype.bootstrap = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.logger.debug('Executing bootstrap.service.base.ts:bootstrap()');
                        // load our secret
                        return [4 /*yield*/, this.sync.loadSecret()];
                    case 1:
                        // load our secret
                        _a.sent();
                        this.sync
                            .events
                            .pipe(filter(function (event) { return event instanceof SyncEndEvent; }), first())
                            .subscribe(this.onAfterFirstSync.bind(this));
                        this.sync
                            .events
                            .pipe(filter(function (event) { return event instanceof SyncConfigDownloadEvent; }))
                            .subscribe(this.onConfigDownloaded.bind(this));
                        this.scheduleReboot();
                        return [4 /*yield*/, this.setupNtp()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Called after the first time the sync is run (regardless of whether is succeeded or not).
     */
    BootstrapServiceBase.prototype.onAfterFirstSync = function () {
        this.logger.debug('Executing bootstrap.service.base.ts:onAfterFirstSync()');
        this.startControllerClient();
        return Promise.resolve();
    };
    /**
     * Called every time a new configuration from the server is received.
     */
    BootstrapServiceBase.prototype.onConfigDownloaded = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var orientation_1, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.logger.debug('Executing bootstrap.service.base.ts:onConfigDownloaded()');
                        this.startControllerClient();
                        return [4 /*yield*/, this.setupNtp()];
                    case 1:
                        _a.sent();
                        if (!this.platform.isSetMenuOrientationSupported()) return [3 /*break*/, 5];
                        orientation_1 = this.settings.get(SettingKeys.ORIENTATION) === 'SCREEN_ORIENTATION_PORTRAIT'
                            ? MenuOrientation.PORTRAIT
                            : MenuOrientation.LANDSCAPE;
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.platform.setMenuOrientation(orientation_1)];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_1 = _a.sent();
                        this.logger.error("Failed to set menu orientation to " + orientation_1 + ": " + e_1.message);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    BootstrapServiceBase.prototype.startControllerClient = function () {
        var controllerClientEnabled = this.settings.get(SettingKeys.CONTROLLER_CLIENT_ENABLED, false);
        if (controllerClientEnabled && !this.controllerClient.isConnectionStarted) {
            this.controllerClient.connect();
        }
    };
    BootstrapServiceBase.prototype.getUptimeForScheduledReboot = function () {
        var uptime = this.platform.getSystemUptime();
        return uptime !== undefined ? uptime : -1;
    };
    BootstrapServiceBase.prototype.scheduleReboot = function () {
        var _this = this;
        var rebootHour = 4; // Do reboot only from 4:00:00 until 4:59:59
        var checkInterval = 60 * 60 * 1000; // Spread reboot of multiple devices over the reboot hour
        var uptimeThreshold = function () {
            // Use positive infinity to prevent reboot if interval is missing in settings.
            var rebootInterval = _this.settings.get(SettingKeys.REBOOT_INTERVAL, Number.POSITIVE_INFINITY) * 1000;
            // Subtract check interval to be sure not to exceed reboot hour especially for daily reboot.
            return rebootInterval - checkInterval;
        };
        // Subtract some seconds from check interval to be sure
        // to match reboot hour with the not so steady time service.
        interval(checkInterval - 5000)
            .pipe(filter(function () { return _this.time.now().hours() === rebootHour; }), filter(function () { return _this.getUptimeForScheduledReboot() >= uptimeThreshold(); }), first(), tap(function () { return _this.logger.info('Rebooting as scheduled.'); }), delay(5000))
            .subscribe(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () { return tslib_1.__generator(this, function (_a) {
            return [2 /*return*/, this.platform.reboot()];
        }); }); });
    };
    BootstrapServiceBase.prototype.setupNtp = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.platform.isNtpSettingsAccessSupported()) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.platform.setNtpConfig({
                                enabled: true,
                                timeZone: this.settings.get(SettingKeys.TIME_ZONE, 'Europe/Amsterdam'),
                                serverAddress: 'pool.ntp.org',
                            })];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_2 = _a.sent();
                        this.logger.error("Failed to set ntp configuration: " + e_2.message);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return BootstrapServiceBase;
}());
export { BootstrapServiceBase };
